<template>
    <div class="setting-item">
        <div class="setting-header">
            <div class="setting-header-label">Dispenser Colors</div>
            <div class="setting-header-bar"><hr class="header-divider" /></div>

            <div class="dispenser-layout">
            <div class="dispenser-controls">

            <div class="button-bar dispenser-button-bar">
                <button class="button-link" :disabled="isDefaultTheme" @click="onReset" title="Reset colors to default">
                    Default
                </button>
                <button class="button-link" @click="onDownloadColors" title="Download the current colors to a file">
                    Download
                </button>
                <button class="button-link" :disabled="isDirty || isDefaultTheme" @click="onUploadClick" title="Upload colors from a file">
                    Upload
                </button>
                <input ref="fileInput" type="file" accept="application/json,.json" style="display:none" @change="onUploadColors" aria-hidden="true" />
            </div>

            <!-- Theme Manager -->
            <div class="theme-manager">
                <div class="theme-manager-title">Theme Manager</div>
                <div class="theme-manager-row">
                    <select v-model="selectedThemeId" @change="onSelectThemeChange" class="theme-select" aria-label="Select the active theme">
                        <option value="" disabled hidden>Select a theme</option>
                        <option v-for="theme in sortedThemes" :key="theme.globalId" :value="theme.globalId">{{ theme.name }}{{ (isDirty && theme.globalId === selectedThemeId) ? ' *' : '' }}</option>
                        <option :value="newThemeOption">New Theme...</option>
                    </select>
                    <template v-if="isSaveAsNew">
                        <button class="button-link" :disabled="!newThemeName.trim()" @click="onSaveTheme" title="Create a new theme from the current colors and make it active">
                            Save
                        </button>
                        <button class="button-link" @click="onCancelNewTheme" title="Discard the new theme and go back to the previous theme">
                            Cancel
                        </button>
                    </template>
                    <template v-else>
                        <button class="button-link" :disabled="!selectedThemeId || !isDirty" @click="saveActiveTheme" title="Save the color changes to the active theme">
                            Save
                        </button>
                        <button class="button-link" :disabled="!isDirty" @click="onRevert" title="Discard unsaved changes and restore the theme's saved colors">
                            Revert
                        </button>
                        <button class="button-link" :disabled="!selectedThemeId || isDefaultTheme" @click="onDeleteTheme" title="Delete the active theme">
                            Delete
                        </button>
                    </template>
                </div>
                <div class="theme-manager-row" v-if="isSaveAsNew">
                    <input type="text" v-model="newThemeName" @input="onThemeNameInput" class="theme-name-input" placeholder="New theme name" aria-label="New theme name" maxlength="20" />
                </div>
            </div>

            <div class="dispenser-colors-grid">
                <!-- Column headers -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name"></span>
                    <span class="dispenser-col-color dispenser-col-heading">Foreground</span>
                    <span class="dispenser-col-color dispenser-col-heading">Background</span>
                    <span class="dispenser-col-color dispenser-col-heading">Border</span>
                </div>

                <!-- Main Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Primary</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorMainButtonForeground" v-model="p_settings.colorMainButtonForeground" title="Select the primary foreground color" aria-label="Select the primary foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorMainButtonBackground" v-model="p_settings.colorMainButtonBackground" title="Select the primary background color" aria-label="Select the primary background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorMainButtonBorder" v-model="p_settings.colorMainButtonBorder" title="Select the primary border color" aria-label="Select the primary border color"/>
                    </span>
                </div>

                <!-- Numeric Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Secondary</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorNumericButtonForeground" v-model="p_settings.colorNumericButtonForeground" title="Select the secondary foreground color" aria-label="Select the secondary foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorNumericButtonBackground" v-model="p_settings.colorNumericButtonBackground" title="Select the secondary background color" aria-label="Select the secondary background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorNumericButtonBorder" v-model="p_settings.colorNumericButtonBorder" title="Select the secondary border color" aria-label="Select the secondary border color"/>
                    </span>
                </div>

                <!-- Reload Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Alternate</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorReloadButtonForeground" v-model="p_settings.colorReloadButtonForeground" title="Select the alternate foreground color" aria-label="Select the alternate foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorReloadButtonBackground" v-model="p_settings.colorReloadButtonBackground" title="Select the alternate background color" aria-label="Select the alternate background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorReloadButtonBorder" v-model="p_settings.colorReloadButtonBorder" title="Select the alternate border color" aria-label="Select the alternate border color"/>
                    </span>
                </div>

                <!-- Cancel Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Cancel Button</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorCancelButtonForeground" v-model="p_settings.colorCancelButtonForeground" title="Select the cancel button foreground color" aria-label="Select the cancel button foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorCancelButtonBackground" v-model="p_settings.colorCancelButtonBackground" title="Select the cancel button background color" aria-label="Select the cancel button background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorCancelButtonBorder" v-model="p_settings.colorCancelButtonBorder" title="Select the cancel button border color" aria-label="Select the cancel button border color"/>
                    </span>
                </div>

                <!-- Clear Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Clear Button</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorClearButtonForeground" v-model="p_settings.colorClearButtonForeground" title="Select the clear button foreground color" aria-label="Select the clear button foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorClearButtonBackground" v-model="p_settings.colorClearButtonBackground" title="Select the clear button background color" aria-label="Select the clear button background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorClearButtonBorder" v-model="p_settings.colorClearButtonBorder" title="Select the clear button border color" aria-label="Select the clear button border color"/>
                    </span>
                </div>

                <!-- Enter Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Enter Button</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorEnterButtonForeground" v-model="p_settings.colorEnterButtonForeground" title="Select the enter button foreground color" aria-label="Select the enter button foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorEnterButtonBackground" v-model="p_settings.colorEnterButtonBackground" title="Select the enter button background color" aria-label="Select the enter button background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorEnterButtonBorder" v-model="p_settings.colorEnterButtonBorder" title="Select the enter button border color" aria-label="Select the enter button border color"/>
                    </span>
                </div>

                <!-- Bay Button -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Bay Primary</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBayButtonForeground" v-model="p_settings.colorBayButtonForeground" title="Select the bay primary foreground color" aria-label="Select the bay primary foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBayButtonBackground" v-model="p_settings.colorBayButtonBackground" title="Select the bay primary background color" aria-label="Select the bay primary background color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBayButtonBorder" v-model="p_settings.colorBayButtonBorder" title="Select the bay primary border color" aria-label="Select the bay primary border color"/>
                    </span>
                </div>

                <!-- Bay Subtext (foreground + background) -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Bay Secondary</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBaySubtextForeground" v-model="p_settings.colorBaySubtextForeground" title="Select the bay secondary foreground color" aria-label="Select the bay secondary foreground color"/>
                    </span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBaySubtextBackground" v-model="p_settings.colorBaySubtextBackground" title="Select the bay secondary background color" aria-label="Select the bay secondary background color"/>
                    </span>
                    <span class="dispenser-col-color"></span>
                </div>

                <!-- Bay Balls (foreground only) -->
                <div class="dispenser-color-row">
                    <span class="dispenser-col-name">Bay Alternate</span>
                    <span class="dispenser-col-color">
                        <input @change="markDirty" :disabled="isDefaultTheme" type="color" id="colorBayBallsForeground" v-model="p_settings.colorBayBallsForeground" title="Select the bay alternate foreground color" aria-label="Select the bay alternate foreground color"/>
                    </span>
                    <span class="dispenser-col-color"></span>
                    <span class="dispenser-col-color"></span>
                </div>
            </div>

            </div><!-- /dispenser-controls -->

                <!-- Inline screen preview -->
                <div class="dispenser-preview">
                    <div class="dispenser-preview-inner">
                    <div class="theme-manager-title">Preview</div>

                    <div class="preview-tabs">
                        <button v-for="t in previewTabs" :key="t.key" class="preview-tab" :class="{ active: activeTab === t.key }" @click="activeTab = t.key">{{ t.label }}</button>
                    </div>

                    <div class="preview-screen" :class="{ 'preview-screen--dark': activeTab === 'keypad' }">
                        <!-- Main screen: logo + 2x2 navigate buttons -->
                        <template v-if="activeTab === 'main'">
                            <div class="preview-logo-bar">
                                <span class="preview-logo-mark"></span>
                                <span class="preview-logo-text">SELECT<span class="preview-logo-pi">pi</span></span>
                            </div>
                            <div class="preview-main-grid">
                                <div v-for="label in mainButtonLabels" :key="label" class="preview-nav-button" :style="buttonStyle('MainButton')" title="Primary">{{ label }}</div>
                            </div>
                        </template>

                        <!-- Ticket Pin screen: logo + PIN entry + keypad + cancel -->
                        <template v-else-if="activeTab === 'keypad'">
                            <div class="preview-logo-bar">
                                <span class="preview-logo-mark"></span>
                                <span class="preview-logo-text">SELECT<span class="preview-logo-pi">pi</span></span>
                            </div>
                            <div class="preview-pin-header">
                                <span class="preview-pin-label">Enter PIN</span>
                                <span class="preview-pin-input"></span>
                            </div>
                            <div class="preview-keypad">
                                <div v-for="n in keypadKeys" :key="n" class="preview-key" :style="buttonStyle('NumericButton')" title="Secondary">{{ n }}</div>
                                <div class="preview-key" :style="buttonStyle('ClearButton')" title="Clear Button">Clear</div>
                                <div class="preview-key" :style="buttonStyle('NumericButton')" title="Secondary">0</div>
                                <div class="preview-key" :style="buttonStyle('EnterButton')" title="Enter Button">Enter</div>
                            </div>
                            <div class="preview-cancel" :style="buttonStyle('CancelButton')" title="Cancel Button">Cancel</div>
                        </template>

                        <!-- Bay screen: logo + heading + duration cards + cancel -->
                        <template v-else-if="activeTab === 'bay'">
                            <div class="preview-logo-bar">
                                <span class="preview-logo-mark"></span>
                                <span class="preview-logo-text">SELECT<span class="preview-logo-pi">pi</span></span>
                            </div>
                            <div class="preview-bay-heading">How many minutes will you play?</div>
                            <div class="preview-bay-row">
                                <div v-for="d in bayDurations" :key="d.title" class="preview-bay-button" :style="buttonStyle('BayButton')" title="Bay Primary">
                                    <div class="preview-bay-title">{{ d.title }}</div>
                                    <div class="preview-bay-balls" :style="{ color: p_settings.colorBayBallsForeground }" title="Bay Alternate">{{ d.balls }}</div>
                                    <div class="preview-bay-badge" :style="{ color: p_settings.colorBaySubtextForeground, backgroundColor: p_settings.colorBaySubtextBackground }" title="Bay Secondary">{{ d.price }}</div>
                                </div>
                            </div>
                            <div class="preview-bucket-footer">
                                <div class="preview-cancel" :style="buttonStyle('CancelButton')" title="Cancel Button">Cancel</div>
                            </div>
                        </template>

                        <!-- Select Bucket screen: logo + 2x2 bucket buttons + reload (bottom-right) -->
                        <template v-else-if="activeTab === 'bucket'">
                            <div class="preview-logo-bar">
                                <span class="preview-logo-mark"></span>
                                <span class="preview-logo-text">SELECT<span class="preview-logo-pi">pi</span></span>
                            </div>
                            <div class="preview-main-grid">
                                <div v-for="label in bucketLabels" :key="label" class="preview-nav-button" :style="buttonStyle('MainButton')" title="Primary">{{ label }}</div>
                            </div>
                            <div class="preview-bucket-footer">
                                <div class="preview-footer-side"></div>
                                <div class="preview-cancel" :style="buttonStyle('CancelButton')" title="Cancel Button">Cancel</div>
                                <div class="preview-footer-side preview-footer-right">
                                    <div class="preview-reload-corner" :style="buttonStyle('ReloadButton')" title="Alternate">Reload Card</div>
                                </div>
                            </div>
                        </template>

                        <!-- Reload screen: logo + 2x2 reload amount buttons + cancel -->
                        <template v-else-if="activeTab === 'reload'">
                            <div class="preview-logo-bar">
                                <span class="preview-logo-mark"></span>
                                <span class="preview-logo-text">SELECT<span class="preview-logo-pi">pi</span></span>
                            </div>
                            <div class="preview-main-grid">
                                <div v-for="label in reloadLabels" :key="label" class="preview-nav-button" :style="buttonStyle('MainButton')" title="Primary">{{ label }}</div>
                            </div>
                            <div class="preview-bucket-footer">
                                <div class="preview-cancel" :style="buttonStyle('CancelButton')" title="Cancel Button">Cancel</div>
                            </div>
                        </template>
                    </div>

                    <div class="preview-note">
                        Approximate preview of the dispenser screens using the current colors.
                    </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, computed } from "vue";
import { getCurrentInstance, onMounted } from "@vue/runtime-core";
import apiSettings from "@/api/apiSettings";

// Sentinel dropdown value that triggers "save as new theme" mode.
const NEW_THEME_OPTION = "__new__";

// Reserved id of the built-in, immutable DEFAULT theme (matches the server).
const DEFAULT_THEME_ID = "default";

// Theme name: must start with a letter, then up to 19 more letters/digits/spaces (20 max).
const THEME_NAME_REGEX = /^[A-Za-z][A-Za-z0-9 ]{0,19}$/;

// Valid CSS hex color (#rgb or #rrggbb) — used when validating an uploaded theme file.
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

// All color fields captured by a theme (button x {foreground, background, border}).
const COLOR_KEYS = [
    "colorMainButtonForeground", "colorMainButtonBackground", "colorMainButtonBorder",
    "colorNumericButtonForeground", "colorNumericButtonBackground", "colorNumericButtonBorder",
    "colorCancelButtonForeground", "colorCancelButtonBackground", "colorCancelButtonBorder",
    "colorClearButtonForeground", "colorClearButtonBackground", "colorClearButtonBorder",
    "colorEnterButtonForeground", "colorEnterButtonBackground", "colorEnterButtonBorder",
    "colorReloadButtonForeground", "colorReloadButtonBackground", "colorReloadButtonBorder",
    "colorBayButtonForeground", "colorBayButtonBackground", "colorBayButtonBorder",
    "colorBayBallsForeground",
    "colorBaySubtextForeground", "colorBaySubtextBackground",
];

export default {
    props: ["settings"],
    components: {},
    setup(props) {
        const emitter = getCurrentInstance().appContext.app.config.globalProperties.emitter;

        const p_settings = ref(props.settings);

        const themes = ref([]);
        const selectedThemeId = ref("");
        const newThemeName = ref("");
        const fileInput = ref(null);
        const isDirty = ref(false);           // a color changed since the last save/select
        const previousThemeId = ref("");      // the currently-applied theme (for revert on cancel)

        const isSaveAsNew = computed(() => selectedThemeId.value === NEW_THEME_OPTION);
        const isDefaultTheme = computed(() => selectedThemeId.value === DEFAULT_THEME_ID);

        // Themes sorted alphabetically, with the built-in DEFAULT pinned first.
        const sortedThemes = computed(() =>
            [...themes.value].sort((a, b) => {
                if (a.globalId === DEFAULT_THEME_ID) return -1;
                if (b.globalId === DEFAULT_THEME_ID) return 1;
                return (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" });
            })
        );

        // Live screen preview — mirrors how the RangeMasterPi screens use the theme colors.
        const activeTab = ref("main");
        const previewTabs = [
            { key: "main", label: "Main" },
            { key: "keypad", label: "Ticket Pin" },
            { key: "bucket", label: "Select Bucket" },
            { key: "reload", label: "Reload" },
            { key: "bay", label: "Bay" },
        ];
        const mainButtonLabels = ["Credit", "Ticket Pin", "Member Card", "Bay Booking"];
        const bucketLabels = ["Small", "Medium", "Large", "Jumbo"];
        const reloadLabels = ["25 for $20", "50 for $35", "75 for $55", "100 for $70"];
        const keypadKeys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];
        const bayDurations = [
            { title: "30", balls: "50 balls", price: "$10.00" },
            { title: "60", balls: "75 balls", price: "$20.00" },
            { title: "90", balls: "100 balls", price: "$30.00" },
            { title: "120", balls: "125 balls", price: "$40.00" },
        ];

        // Build a CSS style object for a button type from its color fields.
        const buttonStyle = (prefix) => {
            const c = p_settings.value;
            const border = c["color" + prefix + "Border"];
            return {
                color: c["color" + prefix + "Foreground"],
                backgroundColor: c["color" + prefix + "Background"],
                border: border ? "3px solid " + border : "none",
            };
        };

        // A color picker changed — enable the Save Theme button.
        const markDirty = () => {
            isDirty.value = true;
        };

        // Cancel "New Theme..." — discard any edits and go back to the previous theme.
        const onCancelNewTheme = () => {
            newThemeName.value = "";
            selectedThemeId.value = previousThemeId.value;
            const prev = themes.value.find((t) => t.globalId === previousThemeId.value);
            if (prev) {
                COLOR_KEYS.forEach((k) => (p_settings.value[k] = prev[k]));
            }
            isDirty.value = false;
        };

        // Discard unsaved changes and restore the active theme's saved colors.
        const onRevert = () => {
            const selected = themes.value.find((t) => t.globalId === selectedThemeId.value);
            if (!selected) return;
            COLOR_KEYS.forEach((k) => (p_settings.value[k] = selected[k]));
            isDirty.value = false;
        };

        // Restrict typing to: leading letter, then letters/digits/spaces, capped at 20 chars.
        const onThemeNameInput = () => {
            let v = newThemeName.value;
            v = v.replace(/[^A-Za-z0-9 ]/g, "");   // only alphanumeric + space
            v = v.replace(/^[^A-Za-z]+/, "");       // must start with a letter
            if (v.length > 20) v = v.slice(0, 20);
            newThemeName.value = v;
        };

        const loadState = async () => {
            const data = await apiSettings.getDispenserColorThemes();
            themes.value = data.themes;
            selectedThemeId.value = data.selectedThemeGlobalId || "";
            previousThemeId.value = selectedThemeId.value;
            isDirty.value = false;
        };

        onMounted(loadState);

        // "Save colors to new theme...": create a new theme from the current colors and make it active.
        const onSaveTheme = async () => {
            const name = newThemeName.value.trim();
            if (!name) return;
            if (!THEME_NAME_REGEX.test(name)) {
                emitter.emit("show-alert", ["Invalid theme name", "Theme name must start with a letter and contain only letters, numbers, and spaces (max 20 characters)."]);
                return;
            }
            if (name.toLowerCase() === "default") {
                emitter.emit("show-alert", ["Reserved name", "\"DEFAULT\" is a reserved theme name. Please choose another."]);
                return;
            }
            try {
                emitter.emit("server-call-start", "Saving theme...");
                const existing = themes.value.find((t) => t.name.trim().toLowerCase() === name.toLowerCase());
                const theme = { name: name, globalId: existing ? existing.globalId : "" };
                COLOR_KEYS.forEach((k) => (theme[k] = p_settings.value[k]));
                const saved = await apiSettings.saveDispenserColorTheme(theme);
                await apiSettings.setSelectedDispenserTheme(saved.globalId);
                newThemeName.value = "";
                await loadState();
                selectedThemeId.value = saved.globalId;
            } catch (err) {
                console.log("ERROR:", err);
                emitter.emit("show-alert", ["Error saving theme", err]);
            }
            emitter.emit("server-call-stop");
        };

        // Selecting a theme makes it the active one and loads its colors into the pickers.
        const onSelectThemeChange = async () => {
            if (isSaveAsNew.value) return; // handled by the name box + Save button
            const theme = themes.value.find((t) => t.globalId === selectedThemeId.value);
            if (!theme) return;
            if (isDirty.value && !window.confirm("Discard unsaved color changes and switch themes?")) {
                selectedThemeId.value = previousThemeId.value; // revert the dropdown
                return;
            }
            try {
                emitter.emit("server-call-start", "Applying theme...");
                await apiSettings.setSelectedDispenserTheme(theme.globalId);
                COLOR_KEYS.forEach((k) => (p_settings.value[k] = theme[k]));
                previousThemeId.value = theme.globalId;
                isDirty.value = false;
            } catch (err) {
                console.log("ERROR:", err);
                emitter.emit("show-alert", ["Error selecting theme", err]);
            }
            emitter.emit("server-call-stop");
        };

        const onDeleteTheme = async () => {
            if (!selectedThemeId.value || isSaveAsNew.value) return;
            const selected = themes.value.find((t) => t.globalId === selectedThemeId.value);
            const themeName = selected ? selected.name : "this theme";
            if (!window.confirm(`Delete theme "${themeName}"? This cannot be undone.`)) return;
            try {
                emitter.emit("server-call-start", "Deleting theme...");
                await apiSettings.deleteDispenserColorTheme(selectedThemeId.value);
                // Re-resolve the active theme (server re-selects an existing theme, or seeds a Default).
                const colors = await apiSettings.getDispenserColors();
                COLOR_KEYS.forEach((k) => (p_settings.value[k] = colors[k]));
                await loadState();
            } catch (err) {
                console.log("ERROR:", err);
                emitter.emit("show-alert", ["Error deleting theme", err]);
            }
            emitter.emit("server-call-stop");
        };

        const onDownloadColors = () => {
            const data = {};
            COLOR_KEYS.forEach((k) => (data[k] = p_settings.value[k]));

            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = "dispenser-colors.json";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        };

        const onUploadClick = () => {
            if (fileInput.value) fileInput.value.click();
        };

        const onUploadColors = async (event) => {
            const file = event.target.files && event.target.files[0];
            event.target.value = ""; // allow re-selecting the same file later
            if (!file) return;

            let data;
            try {
                data = JSON.parse(await file.text());
            } catch (err) {
                emitter.emit("show-alert", ["Invalid color file", "The selected file is not valid JSON."]);
                return;
            }

            for (const k of COLOR_KEYS) {
                if (!HEX_COLOR_REGEX.test((data[k] || "").toString())) {
                    emitter.emit("show-alert", ["Invalid color file", `Missing or invalid color value for "${k}".`]);
                    return;
                }
            }

            // Load the uploaded colors into the pickers; click Save Theme to persist.
            COLOR_KEYS.forEach((k) => (p_settings.value[k] = data[k]));

            // When creating a new theme with no name yet, default it to the file name
            // (dropping the extension and sanitizing to the theme-name rules).
            if (isSaveAsNew.value && !newThemeName.value.trim()) {
                newThemeName.value = file.name
                    .replace(/\.[^.]+$/, "")          // drop extension
                    .replace(/[-_]+/g, " ")            // separators -> spaces
                    .replace(/[^A-Za-z0-9 ]/g, "")     // only letters/digits/spaces
                    .replace(/^[^A-Za-z]+/, "")        // must start with a letter
                    .trim()
                    .slice(0, 20);
            }

            isDirty.value = true;
        };

        // Save the current colors into the active (selected) theme.
        const saveActiveTheme = async () => {
            if (isSaveAsNew.value) return;
            const selected = themes.value.find((t) => t.globalId === selectedThemeId.value);
            if (!selected) return;
            try {
                emitter.emit("server-call-start", "Saving...");
                const theme = { name: selected.name, globalId: selected.globalId };
                COLOR_KEYS.forEach((k) => (theme[k] = p_settings.value[k]));
                await apiSettings.saveDispenserColorTheme(theme);
                Object.assign(selected, theme); // keep the in-memory theme cache in sync
                isDirty.value = false;
            } catch (err) {
                console.log("ERROR:", err);
                emitter.emit("show-alert", ["Error saving theme", err]);
            }
            emitter.emit("server-call-stop");
        };

        const onReset = async () => {
            p_settings.value.colorMainButtonForeground = "#FFFFFF"
            p_settings.value.colorMainButtonBackground = "#3c7458"
            p_settings.value.colorMainButtonBorder = "#FFFFFF"
            p_settings.value.colorNumericButtonForeground = "#000000"
            p_settings.value.colorNumericButtonBackground = "#f7f4f0"
            p_settings.value.colorNumericButtonBorder = "#f7f4f0"
            p_settings.value.colorCancelButtonForeground = "#FFFFFF"
            p_settings.value.colorCancelButtonBackground = "#A63C3C"
            p_settings.value.colorCancelButtonBorder = "#A63C3C"
            p_settings.value.colorClearButtonForeground = "#000000"
            p_settings.value.colorClearButtonBackground = "#D9821F"
            p_settings.value.colorClearButtonBorder = "#D9821F"
            p_settings.value.colorEnterButtonForeground = "#000000"
            p_settings.value.colorEnterButtonBackground = "#6FAF8F"
            p_settings.value.colorEnterButtonBorder = "#6FAF8F"
            p_settings.value.colorReloadButtonForeground = "#000000"
            p_settings.value.colorReloadButtonBackground = "#FFFFFF"
            p_settings.value.colorReloadButtonBorder = "#3c7458"
            p_settings.value.colorBayButtonForeground = "#000000"
            p_settings.value.colorBayButtonBackground = "#FFFFFF"
            p_settings.value.colorBayButtonBorder = "#d72c87"
            p_settings.value.colorBayBallsForeground = "#000000"
            p_settings.value.colorBaySubtextForeground = "#FFFFFF"
            p_settings.value.colorBaySubtextBackground = "#229C33"

            isDirty.value = true;
        }

        return {
            p_settings,
            onReset,
            themes,
            selectedThemeId,
            sortedThemes,
            newThemeName,
            newThemeOption: NEW_THEME_OPTION,
            isSaveAsNew,
            isDefaultTheme,
            isDirty,
            activeTab,
            previewTabs,
            mainButtonLabels,
            bucketLabels,
            reloadLabels,
            keypadKeys,
            bayDurations,
            buttonStyle,
            fileInput,
            markDirty,
            saveActiveTheme,
            onRevert,
            onCancelNewTheme,
            onThemeNameInput,
            onSaveTheme,
            onSelectThemeChange,
            onDeleteTheme,
            onDownloadColors,
            onUploadClick,
            onUploadColors,
        };
    }
};
</script>

<style>
.dispenser-colors-button {
    width: 120px;
}

.dispenser-layout {
    display: flex;
    flex-direction: row;
    gap: 72px;
    align-items: flex-start;
    flex-wrap: wrap;
}

.dispenser-controls {
    flex: 0 1 auto;
    min-width: 340px;
}

.dispenser-preview {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    margin-top: 132px;
}

.dispenser-preview-inner {
    width: 580px;
    max-width: 100%;
}

.preview-tabs {
    display: flex;
    gap: 4px;
    margin-bottom: 10px;
}

.preview-tab {
    flex: 1;
    padding: 6px 0;
    border: 1px solid #cccccc;
    background: #f4f4f2;
    border-radius: 6px 6px 0 0;
    cursor: pointer;
    font-size: 9.5pt;
    color: #444444;
}

.preview-tab.active {
    background: #ffffff;
    border-bottom-color: #ffffff;
    font-weight: 700;
    color: #111111;
}

.preview-screen {
    /* Kiosk main screen (RangeMasterPi is 800 x 475) */
    width: 100%;
    height: 420px;
    background: #ffffff;
    border: 1px solid #dddddd;
    border-radius: 6px;
    padding: 16px 40px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    overflow: hidden;
}

.preview-screen--dark {
    background: #3d3d3d;
    border-color: #3d3d3d;
}

.preview-screen--dark .preview-logo-text,
.preview-screen--dark .preview-logo-pi {
    color: #ffffff;
}

.preview-pin-header {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 18px;
    width: 100%;
    margin-bottom: 14px;
}

.preview-pin-label {
    font-weight: 800;
    font-size: 20pt;
    color: #f0f0f0;
}

.preview-pin-input {
    width: 230px;
    height: 30px;
    background: #ffffff;
    border: 1px solid #bbbbbb;
    border-radius: 16px;
}

.preview-keypad {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 72%;
}

.preview-key {
    text-align: center;
    padding: 12px 0;
    border-radius: 10px;
    font-weight: 700;
    font-size: 14pt;
}

.preview-cancel {
    margin-top: 14px;
    width: 30%;
    text-align: center;
    padding: 11px 0;
    border-radius: 10px;
    font-weight: 700;
    font-size: 14pt;
}

.preview-bay-heading {
    font-weight: 800;
    font-size: 18pt;
    color: #222222;
    text-align: center;
    margin-bottom: 18px;
}

.preview-bay-row {
    display: flex;
    gap: 12px;
    width: 100%;
    justify-content: center;
    margin-top: 4px;
}

.preview-bay-button {
    flex: 1 1 0;
    max-width: 135px;
    min-height: 185px;
    border-radius: 14px;
    padding: 16px 8px;
    box-sizing: border-box;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.preview-bay-title {
    font-weight: 800;
    font-size: 28pt;
    line-height: 1;
}

.preview-bay-balls {
    font-size: 10pt;
    margin-top: 8px;
}

.preview-bay-badge {
    margin-top: auto;
    width: 85%;
    border-radius: 6px;
    padding: 5px 0;
    font-weight: 700;
    font-size: 11pt;
}

.preview-bucket-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin-top: auto;
}

.preview-footer-side {
    flex: 1;
    display: flex;
}

.preview-footer-right {
    justify-content: flex-end;
}

.preview-bucket-footer .preview-cancel {
    margin-top: 0;
}

.preview-reload-corner {
    padding: 11px 26px;
    border-radius: 14px;
    font-weight: 700;
    font-size: 13pt;
    text-align: center;
}

.preview-logo-bar {
    display: flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    margin-bottom: 14px;
}

.preview-logo-mark {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: radial-gradient(circle at 32% 30%, #8bd45a, #2e7d32);
    flex: 0 0 auto;
}

.preview-logo-text {
    font-weight: 800;
    font-size: 14pt;
    color: #111111;
    letter-spacing: 0.5px;
}

.preview-logo-pi {
    font-style: italic;
    font-weight: 600;
    color: #333333;
    margin-left: 1px;
}

.preview-main-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
    width: 100%;
}

.preview-nav-button {
    min-height: 130px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 16pt;
    box-sizing: border-box;
}

.preview-note {
    margin-top: 12px;
    font-size: 8.5pt;
    color: #888888;
    text-align: center;
    line-height: 1.4;
}

.button-bar.dispenser-button-bar {
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    height: auto;
    gap: 16px;
    margin-bottom: 10px;
}

.theme-manager {
    display: flex;
    flex-direction: column;
    padding-left: 5%;
    margin: 10px 0 20px 0;
}

.theme-manager-title {
    text-transform: uppercase;
    font-size: 9pt;
    font-weight: 400;
    margin-bottom: 8px;
}

.theme-manager-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin: 6px 0;
}

.theme-manager-row button {
    white-space: nowrap;
}

.theme-select,
.theme-name-input {
    width: 220px;
    padding: 4px;
}

.dispenser-colors-grid {
    display: flex;
    flex-direction: column;
    padding-left: 5%;
}

.dispenser-color-row {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin: 8px 0;
}

.dispenser-col-name {
    width: 200px;
}

.dispenser-col-color {
    width: 120px;
    text-align: center;
}

.dispenser-col-heading {
    font-weight: 400;
    text-transform: uppercase;
    font-size: 9pt;
}
</style>
